import { gsap } from 'gsap';
import { ISDESKTOP } from '../constants/commonConstants';

const togglerGroups = {};
const groupOverlayListeners = {};

class Toggler {
    constructor(el) {
        this.DOM = {el: el};
        this.DOM.observeable = this.DOM.el.dataset.togglerObserve;
        this.DOM.targets = Array.from(document.querySelectorAll(this.DOM.observeable));
        this.DOM.animateableTargets = Array.from(this.DOM.el.querySelectorAll(this.DOM.el.dataset.togglerAnimate));

        /* Scope can be document for event delegation or array of targets */
        this.scope = this.DOM.el.hasAttribute('data-toggler-delegate') ? document : this.DOM.targets;
        this.event = this.DOM.el.dataset.event || 'click';
        this.togglerClass = this.DOM.el.dataset.togglerClass || 'is-open';
        this.observerClass = this.DOM.el.dataset.observerClass || 'is-active';
        this.closeOnOutside = this.DOM.el.dataset.closeOnOutside || false;
        this.easing = {duration: 0.5, ease: 'circ.inOut'};
        this.group = this.DOM.el.dataset.togglerGroup || null;

        if (this.group) {
            if (!togglerGroups[this.group]) togglerGroups[this.group] = [];
            togglerGroups[this.group].push(this);
        }

        this.initEvents();
    }
    initEvents() {
        /* Check if scope is array of items or document */
        if (Array.isArray(this.scope)) {
            this.scope.forEach((scope) => scope.addEventListener(this.event, (event) => this.toggle(event)));
        } else {
            /* If document send 'delegated' event type */
            this.scope.addEventListener(this.event, (event) => this.toggle(event, 'delegated'));
        }

        document.addEventListener('keyup', (event) => this.toggle(event, undefined, 'close'));

        if (this.group) {
            this.initGroupListeners();
        }
    }
    initGroupListeners() {
        if (groupOverlayListeners[this.group]) return;
        groupOverlayListeners[this.group] = true;

        const overlay = document.querySelector(`[data-toggler-group-overlay="${this.group}"]`);
        if (overlay) {
            overlay.addEventListener('click', () => {
                Toggler.closeGroup(this.group);
            });
        }

        document.addEventListener('click', (event) => {
            const group = togglerGroups[this.group];
            if (!group) return;

            const openToggler = group.find((t) => t.DOM.el.classList.contains(t.togglerClass));
            if (!openToggler) return;

            const clickedInsideDropdown = group.some((t) => t.DOM.el.contains(event.target));
            const clickedOnTarget = group.some((t) => t.DOM.targets.some((target) => target.contains(event.target)));

            if (!clickedInsideDropdown && !clickedOnTarget) {
                Toggler.closeGroup(this.group);
            }
        });
    }
    static closeGroup(groupName) {
        const group = togglerGroups[groupName];
        if (!group) return;

        group.forEach((t) => t.removeClasses());

        const overlay = document.querySelector(`[data-toggler-group-overlay="${groupName}"]`);
        if (overlay) overlay.classList.remove('is-active');
    }
    toggle(event, eventType, action) {
        /* Check if delegated event type */
        const eventTarget = eventType === 'delegated' ? event.target : event.currentTarget;

        if (event.keyCode === 27 && action === 'close') {
            if (this.group) {
                Toggler.closeGroup(this.group);
            } else {
                this.removeClasses();
            }
            return;
        }

        if (!eventTarget || eventTarget === document) return;

        if (eventTarget.matches ? eventTarget.matches(this.DOM.observeable) : eventTarget.msMatchesSelector(this.DOM.observeable)) {
            event.preventDefault();

            this.closeGroupSiblings();

            this.DOM.el.classList.toggle(this.togglerClass);
            this.DOM.targets.forEach((target) => target.classList.toggle(this.observerClass));

            const isExpanded = this.DOM.el.classList.contains(this.togglerClass);
            this.DOM.targets.forEach((target) => {
                if (target.hasAttribute('aria-expanded')) {
                    target.setAttribute('aria-expanded', String(isExpanded));
                }
            });

            this.updateGroupOverlay();
        }

        if (this.closeOnOutside && eventTarget !== this.DOM.el && eventTarget !== this.DOM.target) {
            this.removeClasses();
        }

        const menuIsActive = this.DOM.el.classList.contains(this.togglerClass);
        if (menuIsActive && this.DOM.animateableTargets !== null && !ISDESKTOP) {
            this.animateTargets()
        }
    }
    removeClasses() {
        this.DOM.el.classList.remove(this.togglerClass);
        this.DOM.targets.forEach((target) => {
            target.classList.remove(this.observerClass);

            if (target.hasAttribute('aria-expanded')) {
                target.setAttribute('aria-expanded', 'false');
            }
        });
    }
    closeGroupSiblings() {
        if (!this.group || !togglerGroups[this.group]) return;

        togglerGroups[this.group].forEach((sibling) => {
            if (sibling !== this) sibling.removeClasses();
        });
    }
    updateGroupOverlay() {
        if (!this.group) return;

        const overlay = document.querySelector(`[data-toggler-group-overlay="${this.group}"]`);
        if (!overlay) return;

        const anyOpen = togglerGroups[this.group].some((t) => t.DOM.el.classList.contains(t.togglerClass));
        overlay.classList.toggle('is-active', anyOpen);
    }
    animateTargets() {
        gsap.set(this.DOM.animateableTargets, { opacity: 0, x: -100, ...this.easing });
        gsap.to(this.DOM.animateableTargets, { x: 0, stagger: 0.025, ...this.easing });
        gsap.to(this.DOM.animateableTargets, { opacity: 1, delay: 0.075, stagger: 0.025, ...this.easing, clearProps: "transform" });
    }
}

const togglerNodes = Array.from(document.querySelectorAll('[data-toggler]'));
if (togglerNodes !== null) {
    togglerNodes.forEach((toggler) => { new Toggler(toggler); })
}
